import { z } from 'zod';
import {
  BasisPoints,
  CurrencyCode,
  Email,
  Instant,
  Ok,
  PhoneE164,
  UserPreferences,
  Uuid,
} from '../common';
import { CountryCode, SuspensionReason, TaxKind, VatStatus } from '../enums';
import { defineEndpoint } from '../http';
import { OrganizationSummary } from '../models';
import { patchSchema } from '../patch';

export const getOrganization = defineEndpoint({
  method: 'GET',
  path: '/c/organization',
  mount: 'partner',
  summary: 'Organization, payout-onboarding status and QR generation allowance (WEB-006, WEB-014)',
  response: OrganizationSummary.extend({
    payoutRequirements: z.array(z.string().max(200)),
    /**
     * Every reason the organization is Suspended, from the one shared
     * derivation (`suspensionReasons`). The getting-started checklist's payout
     * row is done exactly when neither ONBOARDING nor VAT_STATUS is among them,
     * so it cannot tick while the organization is still suspended for it.
     */
    suspendedFor: z.array(SuspensionReason),
    /**
     * Whether the owner has sent Stripe everything its form asked, as Stripe
     * last said. It is what tells "not finished" from "Stripe is checking":
     * the platform marks an account PENDING the moment its first link is
     * minted, and Stripe calls a half-filled one restricted, so neither state
     * says it (WEB-006). False while no account exists.
     */
    payoutDetailsSubmitted: z.boolean(),
    /** Null for a bakery organization: it earns a share, not a commission (BAK-028). */
    commissionRateBasisPoints: BasisPoints.nullable(),
    currency: CurrencyCode,
    /**
     * The address a partner writes to the platform at - a request for more QR
     * codes opens a message to it. Derived from the platform's own domain on
     * the server, so the dashboard names no host (OPS-019).
     */
    supportEmail: Email,
  }),
});

export const createPayoutOnboardingLink = defineEndpoint({
  method: 'POST',
  path: '/c/organization/payout-onboarding',
  mount: 'partner',
  summary: 'Payment-provider onboarding link; the real gate on self-serve (WEB-006)',
  body: z.object({
    returnPath: z.string().max(200),
    refreshPath: z.string().max(200),
    /**
     * The signed-in person's own name, as they gave it at sign-up, so the
     * provider's form does not ask a private individual or sole proprietor for
     * it again. Used once, when the account is created; ignored for a company
     * and never stored by the platform.
     */
    payee: z
      .object({
        givenName: z.string().trim().min(1).max(80),
        surname: z.string().trim().min(1).max(80),
      })
      .optional(),
  }),
  idempotent: false,
  response: z.object({ url: z.url(), expiresAt: Instant }),
});

// ---------------------------------------------------------------------------
// Access management and settings (PRD CON-007, CON-008, CON-010)
// ---------------------------------------------------------------------------

/**
 * The domain the platform mints the owner row's address under.
 *
 * There is one member the platform has no address for: the organization's
 * owner, whose credential lives in Clerk and never here. `email` is required
 * all the same - the contract declares one and the response is validated
 * against it - so the worker mints a placeholder, and `.invalid` is what makes
 * it safe: RFC 6761 reserves that TLD and guarantees it never resolves.
 *
 * It is named HERE rather than in the worker that mints it because the reader
 * needs it too. An address that is guaranteed to reach nobody identifies
 * nobody, so a surface that prints one is printing noise at a person - which
 * is what the partner portal's Team screen did, under a column headed Member.
 * One definition, two consumers: the worker composes addresses with it, the
 * portal recognises them by it.
 */
export const PLACEHOLDER_EMAIL_DOMAIN = 'partner.invalid';

/** Whether this address is one the platform minted for want of a real one. */
export function isPlaceholderEmail(email: string): boolean {
  return email.toLowerCase().endsWith(`@${PLACEHOLDER_EMAIL_DOMAIN}`);
}

/**
 * One person in a partner organization. There is no role on it (founder,
 * 2026-09-23): an organization has one kind of member, and every member may do
 * everything the portal offers that organization.
 */
export const PartnerMember = z.object({
  id: Uuid,
  email: Email,
  /**
   * The person's own name, and null whenever the platform does not know it -
   * which it does not for an invitation nobody has accepted, and does not for
   * the synthesized owner row either. It is never the ORGANIZATION's name: a
   * house is not a member of itself, and seeding it that way put a Clerk
   * organization id in this column on every self-serve org (state.ts).
   */
  name: z.string().max(160).nullable(),
  status: z.enum(['INVITED', 'ACTIVE', 'DISABLED']),
  invitedAt: Instant,
});
export type PartnerMember = z.infer<typeof PartnerMember>;

export const listMembers = defineEndpoint({
  method: 'GET',
  path: '/c/members',
  mount: 'partner',
  summary: 'The people in the organization (CON-007)',
  response: z.object({ members: z.array(PartnerMember) }),
});

/*
 * A member's access is changed in the identity provider itself (CON-007): an
 * invitation is emailed by Clerk, a disabled member's membership is removed
 * (the row stays, DISABLED, and enabling sends a new invitation), and a removed
 * member loses both. The bakery's Team screen offers all three; a property
 * organization reads its team as a list.
 */
export const inviteMember = defineEndpoint({
  method: 'POST',
  path: '/c/members',
  mount: 'partner',
  summary: 'Invite a member (CON-007)',
  body: z.object({ email: Email }),
  idempotent: true,
  response: PartnerMember,
});

export const updateMember = defineEndpoint({
  method: 'PATCH',
  path: '/c/members/:memberId',
  mount: 'partner',
  summary: 'Disable or re-enable a member (CON-007)',
  params: z.object({ memberId: Uuid }),
  body: z.object({
    status: z.enum(['ACTIVE', 'DISABLED']).optional(),
  }),
  idempotent: false,
  response: PartnerMember,
});

export const removeMember = defineEndpoint({
  method: 'DELETE',
  path: '/c/members/:memberId',
  mount: 'partner',
  summary: 'Remove a member and their access (CON-007)',
  params: z.object({ memberId: Uuid }),
  idempotent: false,
  response: Ok,
});

/**
 * The organization's settings - and no language among them.
 *
 * `locale` lived here until the one-language-per-user change: it was the
 * "notification language", organization-scoped, gated on `settings.write`, and
 * it sat next to a second, browser-scoped interface language on the same card.
 * A member with no write capability could not choose the language they were
 * written to in, and two members of one organization could not choose
 * differently. Both languages are now the single `UserPreferences.locale`
 * (`/c/me/preferences`), which is per person and needs no capability - your own
 * language is not your organization's setting.
 */
export const PartnerSettings = z.object({
  defaultDeliveryInstructions: z.string().max(2000).nullable(),
  escalationEmail: Email.nullable(),
  escalationPhone: PhoneE164.nullable(),
  /** PRD CON-010 - order activity is notified by default; a daily digest is fine. */
  alerts: z.object({
    orderActivity: z.enum(['NONE', 'FIRST_ORDER', 'EVERY_ORDER', 'DAILY_DIGEST']),
    deliveryFailure: z.boolean(),
    complaint: z.boolean(),
    accessProblem: z.boolean(),
    payoutIssued: z.boolean(),
  }),
});
export type PartnerSettings = z.infer<typeof PartnerSettings>;

/**
 * A PATCH of the organization's settings: one level per group, `alerts` the
 * one nested group. A PATCH of one alert must not require the caller to resend
 * the other four - a client that omitted them would otherwise be rejected, or
 * worse, a client that sent stale ones would silently reset preferences it
 * never touched.
 *
 * `patchSchema` is deliberately SHALLOW, so the nested group gets its own
 * patch schema and its own merge - the recipe stated at the top of
 * `../patch`, which is one rule applied twice rather than a deep-merge mode.
 * Named so the command that applies it (`makeUpdatePartnerSettings`) takes
 * THIS type: a field the contract accepts cannot be dropped on the floor by a
 * hand-written twin of it.
 */
export const PartnerSettingsPatch = patchSchema(PartnerSettings).extend({
  alerts: patchSchema(PartnerSettings.shape.alerts).optional(),
});
export type PartnerSettingsPatch = z.infer<typeof PartnerSettingsPatch>;

export const getSettings = defineEndpoint({
  method: 'GET',
  path: '/c/settings',
  mount: 'partner',
  summary: 'Service controls, default instructions and alert preferences (CON-008, CON-010)',
  response: PartnerSettings,
});

export const updateSettings = defineEndpoint({
  method: 'PATCH',
  path: '/c/settings',
  mount: 'partner',
  summary: 'Update service controls and alert preferences (CON-008, CON-010)',
  body: PartnerSettingsPatch,
  idempotent: false,
  response: PartnerSettings,
});

/**
 * The signed-in member's own preferences (NFR-006).
 *
 * `/me`, not `/c/settings`: this is keyed by the person, not the organization, so
 * it is the same answer whichever of their organizations they are looking at and
 * it carries no capability check. Both partner kinds reach it - a property owner
 * and a bakery house sign into the same portal (BAK-028) and pick their language
 * the same way.
 */
export const getPreferences = defineEndpoint({
  method: 'GET',
  path: '/c/me/preferences',
  mount: 'partner',
  summary: "The signed-in member's own language (NFR-006)",
  response: UserPreferences,
});

export const updatePreferences = defineEndpoint({
  method: 'PATCH',
  path: '/c/me/preferences',
  mount: 'partner',
  summary: "Change the signed-in member's own language (NFR-006)",
  body: patchSchema(UserPreferences),
  idempotent: false,
  response: UserPreferences,
});

// ---------------------------------------------------------------------------

// Legal identity (plan mode-a-menu-and-pricing, section 15)
//
// What an invoice needs from THIS party. The platform will issue two invoice
// series - its commission invoice to each bakery house, and the self-billed
// invoice it issues on behalf of a property partner - and both name
// two businesses. An invoice missing an identifier is not a late invoice; it is
// an invalid document.
//
// The PARTNER enters it, about themselves, in their own portal (founder,
// 2026-08-27). It is their SIREN, their VAT number and their registered office,
// they are the ones who know them, and they are the ones who answer for them
// being right. An operator never types it: Internal reads the block and reports
// what is missing.
//
// One pair of endpoints serves both kinds of partner, because `/c` is
// org-scoped and a bakery org and a property org both sign in here. What differs
// is what is ASKED, and the server says which rather than the screen deciding:
// a property partner is additionally asked their VAT status and, when
// self-billed, offered the self-billing mandate to tick - which nothing waits
// on, the mandate being assumed signed off-platform (founder, 25 September
// 2026); a bakery is neither, since the platform invoices it rather than for it.
// ---------------------------------------------------------------------------

/** Nine digits: the company. Spaces are stripped by the form, never accepted here. */
const Siren = z.string().regex(/^[0-9]{9}$/);
/** Fourteen digits: the establishment. Optional - the invoice is to the company. */
const Siret = z.string().regex(/^[0-9]{14}$/);
/** Intra-Community VAT: a country prefix and up to twelve alphanumerics. */
const VatNumber = z.string().regex(/^[A-Z]{2}[0-9A-Z]{2,12}$/);
/** The INSEE activity code: four digits and a letter. */
const ApeCode = z.string().regex(/^[0-9]{4}[A-Z]$/);

/**
 * The ten fields a partner edits. EVERY ONE IS NULLABLE, permanently.
 *
 * Live organizations carry none of this, and a partner mid-onboarding must be
 * able to save the half they know. Completeness is checked at the moment an
 * invoice is ISSUED, against what that party's kind requires - never here, and
 * never by one rule for everybody, since a private individual legitimately has
 * no SIREN at all. An incomplete block blocks the invoice, never the trade.
 *
 * Declared UNDEFAULTED and separately from the response, which is the shape
 * `patchSchema` asks for: the PATCH is built from these ten and the two
 * self-billing facts are deliberately not among them - they are recorded by an
 * ACT (accepting the mandate), not typed into a box, and their `at` comes from
 * the platform clock and their `by` from the session.
 */
export const LegalIdentityFields = z.object({
  /** As the register spells it, where it differs from the trading name. */
  legalName: z.string().min(1).max(200).nullable(),
  /** In French, as the register spells it: `SAS`, `SARL`, `entreprise individuelle`. */
  legalForm: z.string().min(1).max(120).nullable(),
  siren: Siren.nullable(),
  siret: Siret.nullable(),
  vatNumber: VatNumber.nullable(),
  vatStatus: VatStatus.nullable(),
  /** Address lines, as the platform's own `LegalEntity` holds its office. */
  registeredOffice: z.array(z.string().min(1).max(120)).min(1).max(6).nullable(),
  /** As it is printed: the figure with its currency. */
  shareCapital: z.string().min(1).max(60).nullable(),
  rcsCity: z.string().min(1).max(80).nullable(),
  apeCode: ApeCode.nullable(),
  /**
   * What the payee is for tax purposes, as it answered in the payment step
   * (docs/plans/owner-onboarding.html). Once stated, only an operator changes
   * it: a change of legal person means a new payment account.
   */
  taxKind: TaxKind.nullable(),
  /** Asked of a private individual only. */
  taxResidence: CountryCode.nullable(),
  /** The payment account's country; fixed once that account exists. */
  bankCountry: CountryCode.nullable(),
});
export type LegalIdentityFields = z.infer<typeof LegalIdentityFields>;

export const PartnerLegalIdentity = LegalIdentityFields.extend({
  /**
   * The self-billing mandate (`autofacturation`), as ticked here: a
   * self-billed property partner - registered, or under franchise en base -
   * agrees that the platform issues, in their name, the invoice they would
   * otherwise send it. When, and by whom. Optional: the mandate is assumed
   * signed off-platform (founder, 25 September 2026), so no payout waits on
   * it; a date recorded here is what the partner's self-bills then cite.
   */
  selfBillingAcceptedAt: Instant.nullable(),
  selfBillingAcceptedBy: z.string().max(160).nullable(),
  /**
   * Whether this partner is asked for a VAT status at all, and whether the
   * mandate tick is offered to them (`selfBillingOfferedFor`: exactly when its
   * payout is a self-bill). Answered by the server rather than derived from the
   * organization type by each screen: which document a party gets is the
   * platform's rule, and a rule restated in a frontend is a rule that is wrong
   * in one of the places it lives.
   */
  vatStatusApplies: z.boolean(),
  selfBillingApplies: z.boolean(),
  /**
   * Whether the PLATFORM records this block rather than the partner (OPS-036,
   * CON-013 as amended 2026-09-01). True for a bakery house: operations holds
   * the register facts from the commercial conversation and needs them to
   * create the house's payout account, so the house reads the block here and
   * asks the platform to change it.
   *
   * Stated by the server beside `vatStatusApplies` for the same reason that one
   * is: which party states which fact is the platform's rule, and a screen that
   * switched on its own tenant type would be a second copy of it.
   */
  platformManaged: z.boolean(),
  /**
   * The kind every rule reads: the stated `taxKind`, or, on a row that predates
   * the question, the one the platform derives (`effectiveTaxKind`). Answered
   * here so no screen re-derives it.
   */
  effectiveTaxKind: TaxKind,
  /** True once Stripe has verified the payout account: its country can no longer change. */
  bankCountryLocked: z.boolean(),
  /**
   * True once a PROPERTY partner's payout account is VERIFIED by Stripe (WEB-006): the facts
   * the provider verified - tax status, name, legal form, SIREN, registered
   * office, VAT number, bank country - no longer change. A partner that needs different
   * ones creates a new account. A bakery's block is operations', never locked here.
   */
  identityLocked: z.boolean(),
  /**
   * Where the onboarding and bank-details links are emailed
   * (docs/finance/onboarding.html#collect). For a property partner it is the
   * account creator's own verified address and nothing else (founder,
   * 2026-09-29: "we don't allow for another email"), so it is read here and
   * never written through `/c`; a baker reads the one operations recorded.
   */
  contactEmail: Email.nullable(),
});
export type PartnerLegalIdentity = z.infer<typeof PartnerLegalIdentity>;

export const getLegalIdentity = defineEndpoint({
  method: 'GET',
  path: '/c/legal-identity',
  mount: 'partner',
  summary: "The organization's own legal identity, as an invoice has to print it",
  response: PartnerLegalIdentity,
});

/**
 * What the national business register says about one SIREN
 * (docs/plans/owner-onboarding.html section 4): the payee types nine digits and
 * is asked nothing the register already knows - not even whether it is a sole
 * proprietor or a company.
 */
export const BusinessRegisterEntry = z.object({
  siren: Siren,
  legalName: z.string().min(1).max(200),
  /** In French, as an invoice prints it; null when the register's code is not one we name. */
  legalForm: z.string().min(1).max(120).nullable(),
  taxKind: z.enum(['SOLE_PROPRIETOR', 'COMPANY']),
  registeredOffice: z.array(z.string().min(1).max(120)).min(1).max(6),
  siret: Siret.nullable(),
  /** The register's active VAT number; null for a business under the franchise en base. */
  vatNumber: VatNumber.nullable(),
  apeCode: ApeCode.nullable(),
  /** False for a business the register records as ceased. */
  active: z.boolean(),
});
export type BusinessRegisterEntry = z.infer<typeof BusinessRegisterEntry>;

export const lookupBusinessRegister = defineEndpoint({
  method: 'GET',
  path: '/c/business-register/:siren',
  mount: 'partner',
  summary: 'What the national business register says about one SIREN',
  params: z.object({ siren: Siren }),
  response: z.object({
    /**
     * FOUND: `entry` is the register's. NOT_LISTED: the register answered and
     * does not list it - the facts are typed. UNAVAILABLE: the register did not
     * answer the platform (refused, throttled, timed out), which says nothing
     * about the business; the portal then asks the register itself.
     */
    lookup: z.enum(['FOUND', 'NOT_LISTED', 'UNAVAILABLE']),
    /** The register's answer when `lookup` is FOUND, null otherwise. */
    entry: BusinessRegisterEntry.nullable(),
    /**
     * The VAT number this SIREN would carry, offered only when the register
     * lists none - a proposal the payee confirms, never a fact.
     */
    vatNumberProposed: VatNumber,
  }),
});

export const updateLegalIdentity = defineEndpoint({
  method: 'PATCH',
  path: '/c/legal-identity',
  mount: 'partner',
  summary: "Enter or correct the organization's own legal identity, audited on every edit",
  /**
   * `patchSchema` rather than `.partial()`, because THE RULE
   * (`packages/contracts/src/patch.ts`) is not restated per route: omitted means
   * untouched, an explicit `null` clears, a value sets. A partner who deletes a
   * stale SIRET has no other way to say so, and a partner who saves only their
   * VAT number must not have the other nine fields blanked underneath them.
   *
   * `acceptSelfBilling` is NOT one of the ten fields, because it is not a box:
   * `true` records the mandate as accepted now, by the signed-in member, from
   * the platform clock and the session; `false` withdraws it. Absent, as ever,
   * leaves it alone.
   */
  body: patchSchema(LegalIdentityFields).extend({
    acceptSelfBilling: z.boolean().optional(),
  }),
  idempotent: false,
  response: PartnerLegalIdentity,
});
